import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Icon,
  IconButton,
  styles as ui,
} from "stories/Admin";
import SlidersIcon from "../../SlidersIcon";
import styles from "./OffsetModal.module.scss";

interface OffsetModalProps {
  show: boolean;
  formattedDate: string;
  times: {
    fajrJamatOffset?: number;
    duhrJamatOffset?: number;
    asrJamatOffset?: number;
    maghribJamatOffset?: number;
    ishaJamatOffset?: number;
  };
  dateKey: string;
  onInputChange: (
    dateKey: string,
    offset: keyof OffsetModalProps["times"],
    value: number,
  ) => void;
  onSave: (dateKey: string) => void;
  onClose: () => void;
  fetchLatestPrayerTimes: () => void;
}

const OFFSET_KEYS = [
  "fajrJamatOffset",
  "duhrJamatOffset",
  "asrJamatOffset",
  "maghribJamatOffset",
  "ishaJamatOffset",
] as const;

/** Største offset i minutter (glidebryteren og feltet). */
const MAX_OFFSET = 120;

const clampOffset = (n: number) =>
  Math.min(MAX_OFFSET, Math.max(0, Math.round(n)));

const PRAYER_LABELS: Record<(typeof OFFSET_KEYS)[number], string> = {
  fajrJamatOffset: "Fajr",
  duhrJamatOffset: "Dhuhr",
  asrJamatOffset: "Asr",
  maghribJamatOffset: "Maghrib",
  ishaJamatOffset: "Isha",
};

const OffsetModal: React.FC<OffsetModalProps> = ({
  show,
  formattedDate,
  times,
  dateKey,
  onInputChange,
  onSave,
  onClose,
  fetchLatestPrayerTimes,
}) => {
  const titleId = useId();
  const [checkboxState, setCheckboxState] = useState({
    fajrJamatOffset: true,
    duhrJamatOffset: true,
    asrJamatOffset: true,
    maghribJamatOffset: true,
    ishaJamatOffset: true,
  });

  useEffect(() => {
    setCheckboxState({
      fajrJamatOffset:
        times.fajrJamatOffset === -1 || times.fajrJamatOffset == null,
      duhrJamatOffset:
        times.duhrJamatOffset === -1 || times.duhrJamatOffset == null,
      asrJamatOffset:
        times.asrJamatOffset === -1 || times.asrJamatOffset == null,
      maghribJamatOffset:
        times.maghribJamatOffset === -1 || times.maghribJamatOffset == null,
      ishaJamatOffset:
        times.ishaJamatOffset === -1 || times.ishaJamatOffset == null,
    });
  }, [times]);

  const handleCheckboxChange = (
    offset: keyof typeof checkboxState,
    checked: boolean,
  ) => {
    setCheckboxState((prevState) => ({ ...prevState, [offset]: checked }));
    if (checked) {
      // Deactivate offset
      onInputChange(dateKey, offset, -1);
    } else {
      // Activate offset, default to 0 if undefined
      const currentValue = times[offset] === -1 ? 0 : times[offset] || 0;
      onInputChange(dateKey, offset, currentValue);
    }
  };

  const handleSaveClick = () => {
    onSave(dateKey);
    fetchLatestPrayerTimes();
    onClose();
  };

  if (!show) return null;

  return createPortal(
    <div
      className={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`${ui.modal} ${styles.modal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 className={ui.mh} id={titleId}>
          <span className={ui.mhIcon} aria-hidden="true">
            <SlidersIcon size={18} />
          </span>
          Offset for {formattedDate}
          <IconButton
            label="Lukk"
            icon={<Icon name="x" size={18} />}
            className={styles.close}
            onClick={onClose}
          />
        </h2>
        <div className={ui.mb}>
          {OFFSET_KEYS.map((offsetKey) => {
            const off = checkboxState[offsetKey];
            const value = off ? 0 : times[offsetKey] || 0;
            const label = PRAYER_LABELS[offsetKey];
            return (
              <div key={offsetKey} className={styles.prayer}>
                <div className={styles.line}>
                  <b>{label}</b>
                  <input
                    type="range"
                    min={0}
                    max={MAX_OFFSET}
                    step={1}
                    value={value}
                    disabled={off}
                    aria-label={`Offset for ${label} i minutter`}
                    className={styles.range}
                    style={
                      {
                        "--fill": `${(value / MAX_OFFSET) * 100}%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) =>
                      onInputChange(
                        dateKey,
                        offsetKey,
                        parseInt(e.target.value, 10),
                      )
                    }
                  />
                  <span className={styles.value}>
                    {off ? (
                      <Badge tone="grey">Av</Badge>
                    ) : (
                      <label className={styles.minutes}>
                        <span aria-hidden="true">+</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={MAX_OFFSET}
                          step={1}
                          className={`${ui.inp} ${ui.num} ${styles.minutesInput}`}
                          value={times[offsetKey] ?? 0}
                          aria-label={`Minutter etter bønnetiden for ${label}`}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            onInputChange(
                              dateKey,
                              offsetKey,
                              Number.isNaN(n) ? 0 : clampOffset(n),
                            );
                          }}
                          onFocus={(e) => e.target.select()}
                        />
                        <span>min</span>
                      </label>
                    )}
                  </span>
                </div>
                <Checkbox
                  className={styles.offToggle}
                  label="Slå av offset"
                  checked={off}
                  onChange={(e) =>
                    handleCheckboxChange(offsetKey, e.target.checked)
                  }
                />
              </div>
            );
          })}
          <Alert tone="info">
            I ekstremperioden, når Isha ikke finnes, regnes Isha-offset fra
            Maghrib.
          </Alert>
        </div>
        <div className={ui.mf}>
          <Button variant="secondary" onClick={onClose}>
            Avbryt
          </Button>
          <Button onClick={handleSaveClick}>Lagre</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OffsetModal;
